import React, { Component } from 'react';
import { Box, Text } from 'grommet';
import { Alert, Button, Input, Select, Switch, Table, Tag } from 'antd';
import moment from 'moment';
import { OPS_DATA_URL } from '../config';

const { Option } = Select;
const { Search } = Input;

const getTypeColor = (type) => {
  const normalized = (type || '').toUpperCase();
  if (normalized === 'UPGRADE') return 'blue';
  if (normalized === 'PATCH') return 'green';
  if (normalized === 'HOTFIX') return 'orange';
  return 'default';
};

const toDisplayDate = (row) => {
  if (row.update_time) {
    return moment(Number(row.update_time)).format('YYYY-MM-DD');
  }
  return row.update_date || '-';
};

class AppVersions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: '',
      rows: [],
      farmFilter: 'ALL',
      productFilter: 'ALL',
      typeFilter: 'ALL',
      searchText: '',
      latestOnly: false,
    };
  }

  componentDidMount() {
    this.loadAppVersions();
  }

  loadAppVersions = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const res = await fetch(`${OPS_DATA_URL}/appversions`, { method: 'GET' });
      if (!res.ok) {
        this.setState({ loading: false, error: 'Failed to load app versions data.' });
        return;
      }

      const payload = await res.json();
      const rows = Array.isArray(payload.items) ? payload.items : [];
      this.setState({ loading: false, rows });
    } catch (error) {
      this.setState({ loading: false, error: 'Network error while loading app versions data.' });
    }
  }

  getUniqueValues = (key) => {
    const values = this.state.rows
      .map((row) => (row[key] || '').trim())
      .filter((value) => value.length > 0);
    return Array.from(new Set(values)).sort((left, right) => left.localeCompare(right));
  }

  getFilteredRows = () => {
    const {
      rows,
      farmFilter,
      productFilter,
      typeFilter,
      searchText,
      latestOnly,
    } = this.state;

    const normalizedSearch = searchText.trim().toLowerCase();

    const filtered = rows.filter((row) => {
      if (farmFilter !== 'ALL' && row.farm !== farmFilter) return false;
      if (productFilter !== 'ALL' && row.product !== productFilter) return false;
      if (typeFilter !== 'ALL' && row.type !== typeFilter) return false;

      if (normalizedSearch.length > 0) {
        const haystack = [row.farm, row.product, row.type, row.version, row.update_date]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(normalizedSearch)) return false;
      }

      return true;
    });

    const sorted = filtered.sort((left, right) => {
      const rightTime = Number(right.update_time) || 0;
      const leftTime = Number(left.update_time) || 0;
      if (rightTime !== leftTime) return rightTime - leftTime;
      return `${left.farm}-${left.product}`.localeCompare(`${right.farm}-${right.product}`);
    });

    if (!latestOnly) {
      return sorted;
    }

    const latestByPair = new Map();
    sorted.forEach((row) => {
      const key = `${row.farm}|${row.product}`;
      if (!latestByPair.has(key)) {
        latestByPair.set(key, row);
      }
    });

    return Array.from(latestByPair.values());
  }

  resetFilters = () => {
    this.setState({
      farmFilter: 'ALL',
      productFilter: 'ALL',
      typeFilter: 'ALL',
      searchText: '',
      latestOnly: false,
    });
  }

  render() {
    const {
      loading,
      error,
      farmFilter,
      productFilter,
      typeFilter,
      searchText,
      latestOnly,
      rows,
    } = this.state;

    const farms = this.getUniqueValues('farm');
    const products = this.getUniqueValues('product');
    const types = this.getUniqueValues('type');
    const filteredRows = this.getFilteredRows();

    const columns = [
      {
        title: 'Farm',
        dataIndex: 'farm',
        key: 'farm',
        width: 160,
        sorter: (left, right) => (left.farm || '').localeCompare(right.farm || ''),
      },
      {
        title: 'Product',
        dataIndex: 'product',
        key: 'product',
        width: 180,
        sorter: (left, right) => (left.product || '').localeCompare(right.product || ''),
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 120,
        render: (value) => <Tag color={getTypeColor(value)}>{value || 'N/A'}</Tag>,
        sorter: (left, right) => (left.type || '').localeCompare(right.type || ''),
      },
      {
        title: 'Version',
        dataIndex: 'version',
        key: 'version',
        width: 150,
        render: (value) => value || '-',
        sorter: (left, right) => (left.version || '').localeCompare(right.version || ''),
      },
      {
        title: 'Updated',
        dataIndex: 'update_date',
        key: 'update_date',
        width: 140,
        render: (_, row) => toDisplayDate(row),
        sorter: (left, right) => (Number(left.update_time) || 0) - (Number(right.update_time) || 0),
        defaultSortOrder: 'descend',
      },
    ];

    return (
      <Box width='100%' gap='small'>
        <Box direction='row' justify='between' align='center' wrap gap='small'>
          <Text size='xlarge' weight='bold'>Farm App Versions</Text>
          <Button loading={loading} onClick={this.loadAppVersions}>Refresh</Button>
        </Box>

        {error ? <Alert type='error' message={error} showIcon /> : null}

        <Box direction='row' wrap gap='small' align='center'>
          <Select
            value={farmFilter}
            style={{ minWidth: 180 }}
            onChange={(value) => this.setState({ farmFilter: value })}
          >
            <Option value='ALL'>All Farms</Option>
            {farms.map((farm) => <Option key={farm} value={farm}>{farm}</Option>)}
          </Select>

          <Select
            value={productFilter}
            style={{ minWidth: 180 }}
            onChange={(value) => this.setState({ productFilter: value })}
          >
            <Option value='ALL'>All Products</Option>
            {products.map((product) => <Option key={product} value={product}>{product}</Option>)}
          </Select>

          <Select
            value={typeFilter}
            style={{ minWidth: 160 }}
            onChange={(value) => this.setState({ typeFilter: value })}
          >
            <Option value='ALL'>All Types</Option>
            {types.map((type) => <Option key={type} value={type}>{type}</Option>)}
          </Select>

          <Search
            value={searchText}
            onChange={(event) => this.setState({ searchText: event.target.value })}
            placeholder='Search farm, product, version'
            allowClear
            style={{ minWidth: 260 }}
          />

          <Box direction='row' align='center' gap='xsmall'>
            <Text>Latest only</Text>
            <Switch checked={latestOnly} onChange={(checked) => this.setState({ latestOnly: checked })} />
          </Box>

          <Button onClick={this.resetFilters}>Reset</Button>
        </Box>

        <Text size='small' color='dark-5'>
          Showing {filteredRows.length} rows from {rows.length} total records
        </Text>

        <Table
          rowKey='key'
          loading={loading}
          dataSource={filteredRows}
          columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }}
          scroll={{ x: 900 }}
          size='middle'
        />
      </Box>
    );
  }
}

export default AppVersions;
